import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  LinearProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  TextField,
} from "@mui/material";
import { nanoid } from "nanoid";
import firebase from "firebase/compat/app";

import {
  saveProgressPhotoGroup,
  ProgressPhotoUploadError,
} from "../../utils/progressPhotoUpload";

const ProgressPhotoUploadDialog = ({
  open,
  onClose,
  clientId,
  organizationId,
  progressPhotoConfig = [],
  hasStartingPhotos,
  onUploaded,
}) => {
  const [selectedFiles, setSelectedFiles] = React.useState({});
  const [createdOn, setCreatedOn] = React.useState(() => new Date());
  const [setAsReference, setSetAsReference] = React.useState(
    () => !hasStartingPhotos,
  );
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!open) {
      setSelectedFiles({});
      setCreatedOn(new Date());
      setSetAsReference(!hasStartingPhotos);
      setError(null);
      setUploading(false);
    }
  }, [open, hasStartingPhotos]);

  const handleFileChange = (typeId, event) => {
    const file = event.target.files?.[0];
    setSelectedFiles((prev) => {
      const next = { ...prev };
      if (file) {
        next[typeId] = file;
      } else {
        delete next[typeId];
      }
      return next;
    });
  };

  const poseTypes = progressPhotoConfig.length
    ? progressPhotoConfig
    : [{ id: "front", name: "Front", index: 0 }];

  const selectedPhotos = React.useMemo(
    () =>
      poseTypes
        .filter((pose) => selectedFiles[pose.id])
        .map((pose) => ({
          type: pose.id,
          file: selectedFiles[pose.id],
        })),
    [poseTypes, selectedFiles],
  );

  const hasSelectedPhotos = selectedPhotos.length > 0;

  const handleSubmit = async () => {
    if (!hasSelectedPhotos) {
      setError("Please select at least one photo to upload.");
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const groupId = nanoid();
      const configuration = poseTypes.map(({ id, name, index }) => ({
        id,
        name,
        index,
      }));

      const uploadedByUid = firebase.auth().currentUser?.uid ?? null;

      await saveProgressPhotoGroup({
        organizationId,
        clientId,
        photos: selectedPhotos,
        groupId,
        createdOn,
        configuration,
        uploadedByUid,
      });

      if (setAsReference) {
        await firebase
          .firestore()
          .collection("organizations")
          .doc(organizationId)
          .collection("clients")
          .doc(clientId)
          .set({ startingPhotoGroupId: groupId }, { merge: true });
      }

      onUploaded?.({ groupId, uploadedCount: selectedPhotos.length });
      onClose();
    } catch (err) {
      const message =
        err instanceof ProgressPhotoUploadError
          ? err.userMessage
          : err?.message || "Upload failed";
      setError(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={uploading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload progress photos</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Add one or more photos on behalf of your client. You do not need every
            pose — upload only what you have. Photos appear in the progress gallery
            and weekly reports.
          </Typography>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label="Photo date"
            type="date"
            value={createdOn.toISOString().slice(0, 10)}
            onChange={(e) => {
              const next = new Date(`${e.target.value}T12:00:00`);
              if (!Number.isNaN(next.getTime())) {
                setCreatedOn(next);
              }
            }}
            disabled={uploading}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />

          {poseTypes.map((pose) => (
            <Box key={pose.id}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {pose.name}{" "}
                <Typography component="span" variant="caption" color="text.secondary">
                  (optional)
                </Typography>
              </Typography>
              <Button variant="outlined" component="label" disabled={uploading} fullWidth>
                {selectedFiles[pose.id]
                  ? selectedFiles[pose.id].name
                  : `Choose ${pose.name.toLowerCase()} photo`}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => handleFileChange(pose.id, e)}
                />
              </Button>
            </Box>
          ))}

          <FormControlLabel
            control={
              <Checkbox
                checked={setAsReference}
                onChange={(e) => setSetAsReference(e.target.checked)}
                disabled={uploading}
              />
            }
            label="Set as reference photos for comparisons"
          />

          {uploading ? <LinearProgress /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={uploading || !hasSelectedPhotos}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProgressPhotoUploadDialog;
